/**
 * Invoice numbering (decisions-log M3–M5). Numbers are assigned once, at issue.
 *
 *   sequential_year  `${next}-${YYYY}`, YYYY = year of the invoice period end (work-period year)
 *   legacy_prefix    `${AAA}-${DDMMYYYY}${HHmm}` — letters from payer_text, date = invoice date,
 *                    HHmm = local time of the issue moment (port of legacy generateActNumText)
 */

export const NUMBERING_SCHEMES = ['sequential_year', 'legacy_prefix'] as const;
export type NumberingScheme = (typeof NUMBERING_SCHEMES)[number];

export interface SequentialState {
  readonly next: number;
  readonly resetYearly: boolean;
  readonly lastYear: number | null;
}

export interface SequentialResult {
  readonly number: string;
  readonly state: SequentialState;
}

/** Returns the number to use for `year` and the counter state to persist afterwards. */
export function nextSequentialNumber(state: SequentialState, year: number): SequentialResult {
  const startsOver = state.resetYearly && state.lastYear !== null && year > state.lastYear;
  const current = startsOver ? 1 : state.next;
  return {
    number: `${current}-${year}`,
    state: { next: current + 1, resetYearly: state.resetYearly, lastYear: year },
  };
}

/** Preview without consuming the counter. */
export function previewSequentialNumber(state: SequentialState, year: number): string {
  return nextSequentialNumber(state, year).number;
}

/**
 * First three ASCII letters of the payer text, upper-cased; padded with X; XXX when none.
 * Ported verbatim from legacy generateActNumText (Cyrillic and punctuation are skipped).
 */
export function legacyPrefixLetters(payerText: string | null | undefined): string {
  const letters = (payerText ?? '').match(/[A-Za-z]/g);
  if (!letters || letters.length === 0) return 'XXX';
  if (letters.length >= 3) return letters.slice(0, 3).join('').toUpperCase();
  return letters.join('').toUpperCase().padEnd(3, 'X');
}

/** e.g. ("NORTHWIND OÜ", "2026-09-30", 17:48 local) -> "NOR-300920261748". */
export function legacyPrefixNumber(payerText: string | null | undefined, invoiceDate: string, issuedAt: Date): string {
  const day = invoiceDate.slice(8, 10);
  const month = invoiceDate.slice(5, 7);
  const year = invoiceDate.slice(0, 4);
  const hour = String(issuedAt.getHours()).padStart(2, '0');
  const minute = String(issuedAt.getMinutes()).padStart(2, '0');
  return `${legacyPrefixLetters(payerText)}-${day}${month}${year}${hour}${minute}`;
}
